/**
 * Login Page - Login only (registration disabled, admin creates users)
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import useStore from '../store/useStore';

function playOnlineSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -12; comp.ratio.value = 4;
    master.connect(comp); comp.connect(ctx.destination);
    master.gain.setValueAtTime(0.5, ctx.currentTime);

    const now = ctx.currentTime;
    // Sequence: 3 ascending tones (like "system online" boot sequence)
    const notes = [
      { freq: 330, start: 0,    dur: 0.1 },
      { freq: 495, start: 0.12, dur: 0.1 },
      { freq: 660, start: 0.24, dur: 0.18 },
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0, now + start);
      g.gain.linearRampToValueAtTime(0.9, now + start + 0.01);
      g.gain.setValueAtTime(0.9, now + start + dur - 0.02);
      g.gain.linearRampToValueAtTime(0, now + start + dur);
      osc.connect(g); g.connect(master);
      osc.start(now + start); osc.stop(now + start + dur + 0.01);
    });

    // Final sweep: 400→900 Hz "whoosh" — el momento que entras al sistema
    const osc2 = ctx.createOscillator(), g2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(400, now + 0.46);
    osc2.frequency.exponentialRampToValueAtTime(900, now + 0.72);
    g2.gain.setValueAtTime(0, now + 0.46);
    g2.gain.linearRampToValueAtTime(0.6, now + 0.49);
    g2.gain.linearRampToValueAtTime(0, now + 0.72);
    osc2.connect(g2); g2.connect(master);
    osc2.start(now + 0.46); osc2.stop(now + 0.73);

    // Auto-close AudioContext after sound completes
    setTimeout(() => { try { ctx.close(); } catch(_) {} }, 1200);
  } catch(_) {}
}
import { Zap } from 'lucide-react';


// ── ACCESS GRANTED Visual Effect ─────────────────────────────────────────────
function AccessGrantedVFX({ onDone }) {
  const canvasRef = useRef(null);
  const frameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Responsive: use device pixel ratio for crisp rendering on mobile/retina
    const dpr = window.devicePixelRatio || 1;
    const W = document.documentElement.clientWidth;
    const H = document.documentElement.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    ctx.scale(dpr, dpr);

    const cx = W / 2;
    const cy = H / 2;
    const start = performance.now();
    const duration = 2600; // ms — más tiempo para disfrutarlo

    const glitchChars = '01アイウエオカキクケコサシスセソ#@$%&<>[]{}';
    const randChar = () => glitchChars[Math.floor(Math.random() * glitchChars.length)];

    const colSize = W < 480 ? 14 : 18; // más denso en móvil
    const cols = Math.floor(W / colSize);
    const drops = Array.from({ length: cols }, () => Math.random() * -50);

    // Responsive text sizes
    const mainFontSize = Math.max(18, Math.min(W * 0.075, 52));
    const subFontSize  = Math.max(10, Math.min(W * 0.032, 16));

    function draw(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const fadeOut = progress > 0.75 ? (progress - 0.75) / 0.25 : 0;

      ctx.fillStyle = `rgba(0,0,0,${0.93 - fadeOut * 0.93})`;
      ctx.fillRect(0, 0, W, H);

      if (fadeOut >= 1) { onDone(); return; }

      // Matrix rain
      ctx.font = `${colSize - 4}px monospace`;
      for (let i = 0; i < cols; i++) {
        const x = i * colSize;
        const alpha = Math.max(0, 0.6 - fadeOut * 0.6) * (1 - progress * 0.5);
        ctx.fillStyle = `rgba(0,255,136,${alpha})`;
        ctx.fillText(randChar(), x, drops[i] * colSize);
        drops[i] += 0.7;
        if (drops[i] * colSize > H && Math.random() > 0.97) drops[i] = 0;
      }

      // Expanding rings
      for (let r = 0; r < 4; r++) {
        const rp = Math.min((progress * 1.5 - r * 0.16), 1);
        if (rp <= 0) continue;
        const maxR = Math.sqrt(cx * cx + cy * cy) * 1.1;
        const radius = rp * maxR;
        const alpha = (1 - rp) * (1 - fadeOut) * 0.9;
        ctx.beginPath();
        ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(${r === 0 ? '0,212,255' : '0,255,136'},${alpha})`;
        ctx.lineWidth = r === 0 ? 2.5 : 1.5;
        ctx.stroke();
      }

      // Scan line
      if (progress < 0.72) {
        const scanY = progress * H * 1.4 - H * 0.2;
        const scanAlpha = (1 - fadeOut) * 0.65;
        const grad = ctx.createLinearGradient(0, scanY - 40, 0, scanY + 40);
        grad.addColorStop(0, 'rgba(0,212,255,0)');
        grad.addColorStop(0.5, `rgba(0,212,255,${scanAlpha})`);
        grad.addColorStop(1, 'rgba(0,212,255,0)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, scanY - 40, W, 80);
      }

      // Grid
      if (progress > 0.1 && progress < 0.82) {
        const gridAlpha = Math.min(progress * 3, 1) * (1 - fadeOut) * 0.12;
        ctx.strokeStyle = `rgba(0,212,255,${gridAlpha})`;
        ctx.lineWidth = 0.5;
        const step = W < 480 ? 30 : 40;
        for (let x = 0; x < W; x += step) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
        for (let y = 0; y < H; y += step) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
      }

      // ACCESS GRANTED text
      if (progress > 0.18) {
        const textAlpha = Math.min((progress - 0.18) * 3.5, 1) * (1 - fadeOut);
        const glitch = progress < 0.55 && Math.random() > 0.65;
        const ox = glitch ? (Math.random() - 0.5) * 14 : 0;
        const oy = glitch ? (Math.random() - 0.5) * 7 : 0;

        ctx.shadowColor = '#00D4FF';
        ctx.shadowBlur = 28;
        ctx.font = `bold ${mainFontSize}px monospace`;
        ctx.textAlign = 'center';
        ctx.fillStyle = `rgba(0,212,255,${textAlpha})`;
        ctx.fillText('ACCESS GRANTED', cx + ox, cy - subFontSize * 1.4 + oy);

        ctx.shadowColor = '#00FF88';
        ctx.shadowBlur = 14;
        ctx.font = `${subFontSize}px monospace`;
        ctx.fillStyle = `rgba(0,255,136,${textAlpha * 0.85})`;
        ctx.fillText('ROCKET OPERATIONS SECURE NETWORK', cx, cy + subFontSize * 1.4);

        ctx.shadowBlur = 0;
        ctx.textAlign = 'left';
      }

      frameRef.current = requestAnimationFrame(draw);
    }

    frameRef.current = requestAnimationFrame(draw);
    return () => { if (frameRef.current) cancelAnimationFrame(frameRef.current); };
  }, [onDone]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        width: '100%', height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
}

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVFX, setShowVFX] = useState(false);
  const pendingAuth = useRef(null);
  const { setToken, setUser } = useStore();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post('/api/auth/login', {
        username: form.username,
        password: form.password,
      });
      // Aplica la sesión de inmediato (sin VFX) — entrada limpia y rápida
      playOnlineSound();
      setToken(res.data.token);
      setUser(res.data.user);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showVFX && <AccessGrantedVFX onDone={() => {
        setShowVFX(false);
        if (pendingAuth.current) {
          setToken(pendingAuth.current.token);
          setUser(pendingAuth.current.user);
          pendingAuth.current = null;
        }
      }} />}
      <div
        className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
        style={{
          background: `
            radial-gradient(900px 600px at 80% 20%, rgba(0,122,255,.18), transparent 60%),
            radial-gradient(700px 500px at 20% 80%, rgba(175,82,222,.15), transparent 60%),
            radial-gradient(800px 600px at 50% 50%, rgba(255,45,85,.08), transparent 60%),
            #FAFAFC`,
        }}
      >
        <div className="w-full max-w-md relative z-10">
          {/* Brand mark */}
          <div className="flex items-center justify-center mb-8">
            <div className="glass-pill inline-flex items-center gap-3 p-1.5 pr-5 rounded-full">
              <div className="w-12 h-12 rounded-2xl brand-conic flex items-center justify-center shadow-apple-md">
                <span className="w-9 h-9 rounded-xl bg-white flex items-center justify-center text-lg font-extrabold text-n-800">🚀</span>
              </div>
              <div>
                <div className="text-base font-extrabold tracking-apple-tighter text-n-800 leading-none">RocketTalk</div>
                <div className="text-[10px] font-semibold tracking-widest text-n-500 uppercase mt-1">PTT · Comunicación</div>
              </div>
            </div>
          </div>

          {/* Glass login card */}
          <div className="glass-strong rounded-xl p-8">
            <h2 className="text-2xl font-bold text-n-800 tracking-apple-tighter mb-1">Iniciar sesión</h2>
            <p className="text-sm text-n-600 mb-6">Accede a tu organización</p>

            <form onSubmit={handleSubmit} className="space-y-4" aria-label="Inicio de sesión">
              <div>
                <label htmlFor="rt-login-username" className="block text-xs font-semibold text-n-700 mb-2">Usuario</label>
                <input
                  id="rt-login-username"
                  type="text"
                  value={form.username}
                  onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                  placeholder="tu_usuario"
                  autoComplete="username"
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 rounded-md text-sm text-n-800 placeholder-n-400 outline-none transition-all bg-white"
                  style={{ border: '1px solid rgba(0,0,0,0.10)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)' }}
                  onFocus={e => e.target.style.borderColor = '#007AFF'}
                  onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.10)'}
                />
              </div>

              <div>
                <label htmlFor="rt-login-password" className="block text-xs font-semibold text-n-700 mb-2">Contraseña</label>
                <input
                  id="rt-login-password"
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                  aria-required="true"
                  className="w-full px-4 py-3 rounded-md text-sm text-n-800 placeholder-n-400 outline-none transition-all bg-white"
                  style={{ border: '1px solid rgba(0,0,0,0.10)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)' }}
                  onFocus={e => e.target.style.borderColor = '#007AFF'}
                  onBlur={e => e.target.style.borderColor = 'rgba(0,0,0,0.10)'}
                />
              </div>

              {error && (
                <div className="p-3 rounded-md text-sm font-semibold"
                  style={{ background: 'rgba(255,59,48,0.08)', color: '#FF3B30', border: '1px solid rgba(255,59,48,0.20)' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-md font-bold text-sm text-white transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                style={{
                  background: loading ? '#AEAEB2' : 'linear-gradient(135deg, #007AFF, #AF52DE)',
                  boxShadow: loading ? 'none' : '0 12px 30px rgba(0,122,255,0.35)',
                }}
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <><Zap size={16} /> Entrar</>
                )}
              </button>
            </form>
          </div>

          <p className="text-center text-xs text-n-500 mt-6">
            RocketTalk v2.0 · Solo acceso autorizado
          </p>
        </div>
      </div>
    </>
  );
}